/**
 * NutritionSessionDetailPage — Manager view for a single foliar application session (F3).
 *
 * URL: /nutrition/foliar/sessions/:id (the legacy /nutrition/sessions/:id now redirects here)
 * Guard: RequireMenu fertilizing:sessions  (set in App.tsx)
 *
 * Rebuilt on `pat-*` primitives, reading `nutritionService.getFoliarSessionDetail`
 * (a purpose-built read that already resolves orchard facts, machine tank
 * volume, the session's planned product totals and each combo's applied
 * quantities — see services/nutritionService.ts:FoliarSessionDetail). All
 * eligibility/variance DECISIONS live in ./foliarSessionDetail — this file is
 * presentation only.
 *
 * The planned-vs-applied comparison is SESSION-LEVEL only (R15): a combo's
 * `plannedQuantity` snapshot is the whole session's plan by design (spec
 * §6.2), so the expanded combo row lists applied quantities and nothing else.
 *
 * Two DataTables, and BOTH carry a `mobileCardRow`: the combos roster (who
 * did what, when, with an expand-to-detail row — the one with the "shipped 3
 * times" bug, expand toggle going inert on a phone once a card view exists)
 * and the planned-vs-applied totals table (sessionVariance). The latter gets
 * a card too, deliberately: it is the §2.8 planned-vs-applied comparison,
 * the reason this screen exists, and a phone falling back to a four-column
 * horizontally-scrolling table for it would defeat the point.
 *
 * DataTable.tsx hard-codes `data-testid="data-table-cards"` (and
 * `"data-table-footer"`) on ITS OWN wrapper (same trap as DefinitionList's
 * hard-coded "definition-list" testid) — two DataTables on one page both
 * supplying `mobileCardRow` puts two elements with that exact testid in the
 * DOM simultaneously, and an unscoped `getByTestId('data-table-cards')`
 * throws on multiple matches. Each table is therefore wrapped in its own
 * testid'd element (`fsd-combos` / `fsd-variance`) so every card/footer
 * assertion can scope through `within()` — disambiguating at the call site
 * rather than touching the shared primitive.
 */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import {
  DataTable, DataPair, DefinitionList, Field, FormSection, Modal, Notice, StatusPill,
} from '../patterns';
import type { DataTableColumn, StatusVariant } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import {
  nutritionService,
} from '../../services/nutritionService';
import type {
  FoliarSessionDetail,
} from '../../services/nutritionService';
import {
  nutritionSessionService, getOperators, getMachines,
} from '../../services/nutritionSessionService';
import type {
  NutritionMixPreview, SprayOperator, SprayMachine,
} from '../../services/nutritionSessionService';
import {
  VARIANCE_MARKER_PCT, ComboRow, VarianceLine,
  varianceTone, buildComboRows, sessionVariance, tanksAppliedTotal, comboPlannedTanks,
  canPrintInstruction, canDeleteSession, canCancelCombo, canRecordCombo, waitingForSignoff,
} from './foliarSessionDetail';
import NutritionMixPreviewCard from './NutritionMixPreviewCard';
import NutritionBackCapturePill from './NutritionBackCapturePill';
import { SESSION_STATUS_VARIANT } from './nutritionSessionStatus';
import { describeWeather } from './weatherReadback';
import { formatMeasure } from '../../utils/formatMeasure';
import { formatDateTime } from '../../dateFormat';
import { completeFoliar } from '../../services/nutritionBackCaptureService';
import { getTenantInfo } from '../../services/tenantService';
import { isBackCaptureBlocked } from '../../utils/backCaptureWindow';
import './nutrition.css';

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

/*
 * #518 — one key per MEASURED cause. The old single string named the machine
 * cause for all of them, and for a session with no plannedMix its remedy
 * looped: re-assigning writes no plan when the parent has nothing to derive
 * from. The server measures the cause (the 422 body's `reason` field) and the
 * printed sheet renders the same three; the two surfaces must not disagree.
 */
const PLAN_UNAVAILABLE_KEY: Record<string, string> = {
  'no-session-plan': 'nutrition.sessions.detail.comboPlanUnavailableNoSessionPlan',
  'no-tank-volume': 'nutrition.sessions.detail.comboPlanUnavailableNoTankVolume',
  'assigned-before-plan': 'nutrition.sessions.detail.comboPlanUnavailableAssignedBeforePlan',
};

/*
 * For a reason the server did not send, or one this page does not recognise.
 * It names NO cause, matching the printed sheet's stance for the same state:
 * the page used to fall back to the assigned-before-plan copy, telling the
 * manager "this operator was assigned before the mix was planned" about a
 * cause nothing measured, while the sheet said only that no per-tank dose
 * could be worked out. Two surfaces, one combo, two different stories (D3).
 */
const PLAN_UNAVAILABLE_UNMEASURED_KEY = 'nutrition.sessions.detail.comboPlanUnavailableUnmeasured';

// Combo statuses are a distinct vocabulary from session statuses (SESSION_STATUS_VARIANT
// covers draft/planned/in_progress/completed/incomplete/signed_off/cancelled/deleted; a
// combo is not_started/in_progress/completed/incomplete/cancelled — see FoliarDetailCombo).
const COMBO_STATUS_VARIANT: Record<string, StatusVariant> = {
  not_started: 'queued',
  in_progress: 'warn',
  completed: 'ok',
  incomplete: 'danger',
  cancelled: 'danger',
};

const NutritionSessionDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [detail, setDetail] = useState<FoliarSessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [operators, setOperators] = useState<SprayOperator[]>([]);
  const [machines, setMachines] = useState<SprayMachine[]>([]);
  const [selectedOperator, setSelectedOperator] = useState('');
  const [selectedMachine, setSelectedMachine] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');

  const [mixPreviews, setMixPreviews] = useState<Record<string, NutritionMixPreview>>({});
  // The combos whose mix preview came back 422 `plan-unavailable`, mapped to
  // the MEASURED reason (#518). A Set could only say THAT a combo had no
  // plan; the page then printed one remedy for all three causes, and for
  // `no-session-plan` that remedy LOOPS (re-assigning writes no plan when the
  // parent has nothing to derive from). Keyed by comboId because this is a
  // fact about individual combos, not the session (#515).
  const [planUnavailableCombos, setPlanUnavailableCombos] = useState<Map<string, string>>(new Map());

  const [expandedCombos, setExpandedCombos] = useState<Set<string>>(new Set());

  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState('');

  const [signingOff, setSigningOff] = useState(false);
  const [signOffError, setSignOffError] = useState('');
  // Guards `load()`'s success path from clearing a signOffError that a
  // FAILED sign-off just set — `handleSignOff` always reloads in its
  // `finally` (so combos/session reflect the true server state either way),
  // and load()'s success handler would otherwise blank the error before the
  // user reads it. Exactly FoliarRoundPage.tsx:133,152's pattern.
  const noticeRef = useRef(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Cancel-combo (#476): stand an assigned operator down. cancelTarget holds
  // the row awaiting cancellation (null = modal closed); a typed reason is
  // required, so this cannot reuse handleRemoveCombo's window.confirm.
  const [cancelTarget, setCancelTarget] = useState<ComboRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  // Record (#482) — retrospective capture of a combo, on the session's own
  // page. Replaces the retired Back Capture menu; posts to the SAME endpoint
  // it did, so the 90-day bound and the back_capture provenance markers are
  // unchanged. recordTarget holds the row being captured (null = modal closed).
  const [recordTarget, setRecordTarget] = useState<ComboRow | null>(null);
  const [recordDate, setRecordDate] = useState('');
  const [recordTanks, setRecordTanks] = useState('');
  const [recordBusy, setRecordBusy] = useState(false);
  const [recordError, setRecordError] = useState('');
  // Tenant-level 90-day override. Read once on mount; a failure leaves it
  // false, which is the SAFE default — the user sees the warning and the
  // server remains the authority either way.
  const [beyondOverrideOn, setBeyondOverrideOn] = useState(false);

  useEffect(() => {
    let live = true;
    getTenantInfo()
      .then((tenant: any) => {
        if (!live) return;
        setBeyondOverrideOn(!!(tenant?.applicationSettings?.allowBackCaptureBeyond90Days
          ?? tenant?.spraySettings?.allowBackCaptureBeyond90Days));
      })
      .catch(() => { /* non-fatal: assume the override is off */ });
    return () => { live = false; };
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await nutritionService.getFoliarSessionDetail(id);
      setDetail(data);
      setError('');
      if (!noticeRef.current) setSignOffError('');
    } catch (err: any) {
      setError(err?.response?.data?.error === 'not-foliar-session'
        ? t('nutrition.sessions.detail.notFoliar')
        : t('nutrition.sessions.detail.loadError'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Operator/machine pools for the (minimal) assign form — read-only Protection
  // pool, same as FoliarRoundPage's bulk bar.
  useEffect(() => {
    let live = true;
    Promise.all([getOperators(), getMachines()])
      .then(([ops, machs]) => {
        if (!live) return;
        setOperators(ops);
        setMachines(machs);
      })
      .catch(() => { /* non-fatal: the form just renders with empty selects */ });
    return () => { live = false; };
  }, []);

  /*
   * Per-combo mix preview.
   *
   * A 422 `plan-unavailable` is a fact about ONE COMBO: `buildMixPreview`
   * raises it when that combo carries no `plannedTankCount`/`plannedPerTankMix`,
   * which `assignCombo` only writes when the parent has a plannedMix AND the
   * chosen machine has a tankVolume. So it marks one ROW. It used to set a
   * single session-wide boolean rendered with the OPERATOR's "Do not spray"
   * copy, which condemned a whole session for one bad combo and gave a manager
   * at a desk advice they cannot act on (#515).
   *
   * Cancelled combos are not fetched at all. A stood-down combo still has no
   * plan, so querying it re-raised the 422 on every reload and the warning
   * could never be cleared — removing the operator entirely was the only way
   * out.
   *
   * `abandoned` (not `cancelled`) is the cleanup flag: `cancelled` is what a
   * COMBO is, and this code now distinguishes the two.
   */
  useEffect(() => {
    if (!detail) return;
    let abandoned = false;
    setPlanUnavailableCombos(new Map());
    const next: Record<string, NutritionMixPreview> = {};
    // `liveCombos`, not `live`: a neighbouring effect uses `live` as its
    // mounted-guard flag, and this holds combos. Same reason as `abandoned`
    // above — do not make one name mean two things in this file.
    const liveCombos = detail.combos.filter((c) => c.status !== 'cancelled');
    Promise.all(liveCombos.map((c) => (
      nutritionSessionService.getMixPreview(c.comboId)
        .then((preview) => { next[c.comboId] = preview; })
        .catch((err: any) => {
          if (err?.response?.data?.error === 'plan-unavailable' && !abandoned) {
            // Task 10 measures WHICH of three causes applies and returns it
            // as a discrete `reason` field — branch on that, never on
            // `message` (P2a2/D4: message is a raw server sentence that
            // could be reworded at any time). A missing reason is carried
            // through as the empty string rather than substituted with a
            // cause: it maps to no key, and the render falls to the
            // cause-free copy. Naming one here would assert something
            // nothing measured.
            const reason = err?.response?.data?.reason;
            setPlanUnavailableCombos((prev) => (
              new Map(prev).set(c.comboId, typeof reason === 'string' ? reason : '')
            ));
          }
        })
    ))).then(() => { if (!abandoned) setMixPreviews(next); });
    return () => { abandoned = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.combos]);

  const toggleCombo = useCallback((comboId: string) => {
    setExpandedCombos((prev) => {
      const next = new Set(Array.from(prev));
      if (next.has(comboId)) next.delete(comboId); else next.add(comboId);
      return next;
    });
  }, []);

  const openRecord = (row: ComboRow) => {
    setRecordTarget(row);
    setRecordError('');
    // Everything the form needs is already on the detail read — no second
    // fetch. session.date seeds the date.
    const s = detail?.session;
    setRecordDate(s?.date ? s.date.slice(0, 10) : '');

    /*
     * Seed the tank count ONLY when this session has a single live operator
     * (#506, D3).
     *
     * `comboPlannedTanks(row).exact` is the WHOLE SESSION's tank fraction for
     * that machine, not this operator's share. A foliar session may be worked
     * by several operators, each doing a PORTION, so seeding it on a
     * two-operator session had a manager who accepted the defaults book 2x the
     * plan — which is how all three of the sessions in #506 got there. The
     * manager transcribing the paper logsheet has the real per-operator figure
     * in front of them; make them enter it.
     *
     * Cancelled combos do not count: a session with one live and one
     * stood-down operator has a single operator doing the whole job. This
     * mirrors the server's own fallback rule exactly.
     */
    const liveCombos = (detail?.combos || []).filter((c) => c.status !== 'cancelled');
    const planned = comboPlannedTanks(row);
    const tanks = liveCombos.length === 1 && planned ? (planned.exact ?? planned.count) : null;
    setRecordTanks(tanks === null ? '' : String(tanks));
  };

  const closeRecord = () => {
    setRecordTarget(null);
    setRecordError('');
  };

  const handleRecord = async () => {
    if (!recordTarget) return;
    setRecordBusy(true);
    setRecordError('');
    try {
      /*
       * No `products` (#506, D1). The server recomputes productsConsumed from
       * `tankCount x this combo's dosePerTank` and discards whatever the
       * client sends — spray's rule. Sending the whole session's productTotals
       * per combo, which this modal used to seed, is precisely the defect.
       */
      await completeFoliar(recordTarget.comboId, {
        date: new Date(recordDate).toISOString(),
        tankCount: Number(recordTanks),
      });
      setRecordTarget(null);
      await load();
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        // Someone else completed it. The work is DONE, not failed — closing
        // and reloading tells the truth; a "could not save" would not.
        setRecordTarget(null);
        await load();
        return;
      }
      if (status === 422 && err?.response?.data?.error === 'plan-unavailable') {
        // Distinct from the unit-conflict 422 below, which is also a 422 and
        // would otherwise swallow this with copy naming no products at all.
        setRecordError(t('nutrition.sessions.detail.recordPlanUnavailable'));
        return;
      }
      if (status === 422) {
        const names = (err?.response?.data?.conflicts || [])
          .map((c: any) => c.productName).filter(Boolean).join(', ');
        setRecordError(t('nutrition.sessions.detail.recordUnitConflict', { products: names }));
        return;
      }
      if (status === 400 && err?.response?.data?.error === 'back_capture_too_old') {
        // Client and server disagreed about the window (clock skew, or the
        // tenant toggle changed under us). Show the server's answer. Keyed on
        // `error` because that is the literal the route sends for this one; the
        // two branches below cannot be, see their comment.
        setRecordError(t('nutrition.sessions.detail.recordTooOld'));
        return;
      }
      // Branch on the CODE, never on `error` — routes/nutritionBackCapture.js
      // puts `err.message` in `error` for these, a raw server sentence that
      // changes whenever the message is reworded (#501). Both used to fall
      // through to the generic copy, which names no cause at all.
      const code = err?.response?.data?.code;
      if (status === 400 && (code === 'BAD_DATE' || code === 'BAD_TIMES')) {
        setRecordError(t('nutrition.sessions.detail.recordBadDate'));
        return;
      }
      if (status === 400 && code === 'TANK_COUNT_REQUIRED') {
        // Should be unreachable — the submit gate is numeric (see the button's
        // disabled predicate) — but a 400 falling through to "could not record"
        // names no cause, and defence in depth costs one branch.
        setRecordError(t('nutrition.sessions.detail.recordTanksRequired'));
        return;
      }
      setRecordError(t('nutrition.sessions.detail.recordSaveError'));
    } finally {
      setRecordBusy(false);
    }
  };

  // Breadcrumb — above every early return (a hook below a guard changes hook
  // order and React throws). This page is a sibling of FoliarWorkspaceLayout,
  // not one of its tabs, so it never sees the layout's resolved programme —
  // a bare '/nutrition/foliar' would silently move the manager to whichever
  // programme is currently active. Both programmeId and roundId come from
  // detail.session, so the crumb falls back to label-only / bare targets
  // until it has loaded.
  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('nutrition.title'), to: '/nutrition' },
    {
      label: detail?.session.programmeName || t('nutrition.foliar.workspace.title'),
      to: detail ? `/nutrition/foliar?programme=${detail.session.programmeId}` : '/nutrition/foliar',
    },
    {
      label: detail ? detail.session.roundName : t('common.loading'),
      to: detail ? `/nutrition/foliar/programmes/${detail.session.programmeId}/rounds/${detail.session.roundId}` : undefined,
    },
    { label: detail ? `${detail.session.orchardNo} · ${detail.session.roundName}` : t('common.loading') },
  ]);

  if (loading) {
    return (
      <div className="pat-page" data-testid="fsd-loading">
        <p className="ntr-muted">{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="pat-page" data-testid="fsd-error">
        <Notice tone="danger" testId="fsd-error-notice">{error || t('nutrition.sessions.detail.loadError')}</Notice>
      </div>
    );
  }

  const { session, orchard } = detail;
  const isSignedOff = session.status === 'signed_off';
  const combos = buildComboRows(detail);
  const varianceLines = sessionVariance(detail);
  const totalApplied = round2(varianceLines.reduce((s, l) => s + l.applied, 0));
  const knownPlanned = varianceLines.filter((l) => l.planned != null);
  const totalPlanned = knownPlanned.length === 0
    ? null
    : round2(knownPlanned.reduce((s, l) => s + (l.planned as number), 0));
  const waiting = waitingForSignoff(detail, new Date());

  // (#461/D5) There is no single session-level tank figure any more — two
  // combos on two machines have two different correct answers, so
  // `session.tanksPlanned`/`tanksExact` no longer exist on the wire at all.
  // Each combo's own figure is rendered in the combos table/card below,
  // against the machine it belongs to. Same rule as the mix card and the
  // roster column otherwise: the exact value appears only when it differs
  // from the ceiling, and the dash when the combo has none yet.
  const plannedTanksLabel = (c: ComboRow): string => {
    const planned = comboPlannedTanks(c);
    if (!planned) return '—';
    return planned.exact != null && planned.exact !== planned.count
      ? t('nutrition.sessions.detail.tanksExact', { count: planned.count, exact: planned.exact })
      : String(planned.count);
  };
  const appliedTotal = tanksAppliedTotal(detail);

  const handleAssign = async () => {
    if (!id || !selectedOperator || !selectedMachine) return;
    setAssignError('');
    setAssigning(true);
    try {
      await nutritionSessionService.assignCombo(id, {
        operatorId: selectedOperator,
        machineId: selectedMachine,
      });
      setSelectedOperator('');
      setSelectedMachine('');
      await load();
    } catch {
      setAssignError(t('nutrition.sessions.detail.assignError'));
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveCombo = async (comboId: string) => {
    if (!window.confirm(t('nutrition.sessions.detail.removeComboConfirm'))) return;
    try {
      await nutritionSessionService.removeCombo(comboId);
      await load();
    } catch {
      setAssignError(t('nutrition.sessions.detail.removeComboError'));
    }
  };

  const closeCancelModal = () => {
    setCancelTarget(null);
    setCancelReason('');
    setCancelError('');
  };

  const handleCancelCombo = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    setCancelError('');
    try {
      await nutritionSessionService.cancelCombo(cancelTarget.comboId, cancelReason);
      closeCancelModal();
      await load();
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'LAST_COMBO') {
        setCancelError(t('nutrition.sessions.detail.cancelComboLastError'));
      } else if (code === 'HAS_ACTUALS') {
        setCancelError(t('nutrition.sessions.detail.cancelComboActualsError'));
      } else if (code === 'ALREADY_TERMINAL') {
        // Reachable only via a race — canCancelCombo already hides the
        // affordance for every status that would trip this on the server
        // (stale page state, or a second manager acting on the same combo
        // first). Distinct copy tells the manager to refresh rather than
        // read the generic "try again" as an instruction to retry the same
        // request, which would 409 identically.
        setCancelError(t('nutrition.sessions.detail.cancelComboTerminalError'));
      } else {
        setCancelError(t('nutrition.sessions.detail.cancelComboError'));
      }
    } finally {
      setCancelling(false);
    }
  };

  const handlePrintInstructions = async () => {
    if (!id) return;
    setPrinting(true);
    setPrintError('');
    try {
      await nutritionSessionService.downloadInstructionPdf('foliar', id);
    } catch (err: any) {
      // The sheet 409s (NO_MACHINE_ASSIGNED) whenever no combo is PRINTABLE —
      // none live, or none of the live ones has a stored per-tank plan. A
      // single planless combo does not 409; it prints as a warning beside its
      // planned siblings. canPrintInstruction covers the live-status half only
      // (the plan is not on this page's wire contract), so this catch is the
      // backstop, not a redundant guard.
      // downloadInstructionPdf has no catch of its own, so without this the
      // rejection was unhandled and the user saw nothing at all.
      setPrintError(err?.response?.status === 409
        ? t('nutrition.sessions.detail.printNoMachine')
        : t('nutrition.sessions.detail.printFailed'));
    } finally {
      setPrinting(false);
    }
  };

  const handleSignOff = async () => {
    if (!id) return;
    noticeRef.current = false;
    setSignOffError('');
    setSigningOff(true);
    try {
      await nutritionSessionService.signOff(id);
    } catch {
      noticeRef.current = true;
      setSignOffError(t('nutrition.sessions.detail.signOffError'));
    } finally {
      setSigningOff(false);
      // Re-read the detail rather than splice the returned session in: the
      // session's productTotals/tanksExact and every combo's applied vs.
      // planned figures live only on the GET, never on the signoff response.
      await load();
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await nutritionSessionService.removeSession(id);
      navigate(`/nutrition/foliar/programmes/${session.programmeId}/rounds/${session.roundId}`);
    } catch {
      setDeleteError(t('nutrition.sessions.detail.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  // ONE render function for a combo's expanded detail, called by BOTH
  // expandedRowRender and mobileCardRow, with a distinct testid per call
  // site (jsdom renders both simultaneously — a shared id makes getByTestId
  // ambiguous). This is the defect that has shipped three times in this
  // feature: Patterns.css hides the whole <table> (expandedRowRender's row
  // included) below 768px once mobileCardRow is supplied, so a card whose
  // toggle relies solely on expandedRowRender is inert on a real phone.
  const renderComboDetail = (row: ComboRow, testId: string) => {
    const preview = mixPreviews[row.comboId];
    return (
      <div className="ntr-fsd-combo-detail" data-testid={testId}>
        <div className="ntr-fsd-combo-detail__section">
          <span className="ntr-fsd-combo-detail__heading">
            {t('nutrition.sessions.field.weatherStart')}
          </span>
          <p>{describeWeather(row.weatherStart ?? undefined)}</p>
        </div>
        <div className="ntr-fsd-combo-detail__section">
          <span className="ntr-fsd-combo-detail__heading">
            {t('nutrition.sessions.field.weatherEnd')}
          </span>
          <p>{describeWeather(row.weatherEnd ?? undefined)}</p>
        </div>
        {/*
          APPLIED ONLY — no per-combo planned figure (R15). A combo's
          `plannedQuantity` is the WHOLE session's plan by design (spec §6.2),
          so this list used to read "2.5 kg / 5 kg ⚠" for an operator who did
          exactly their half of the job. The honest basis is the combo's own
          share (plannedTanksExact x dosePerTank) and `dosePerTank` is not on
          this page's wire contract — see foliarSessionDetail.buildAppliedLines.
          The planned-vs-applied comparison lives in the session-level table
          below, where the plan actually is.
        */}
        <div className="ntr-fsd-combo-detail__section">
          <span className="ntr-fsd-combo-detail__heading">
            {t('nutrition.sessions.detail.variance.colApplied')}
          </span>
          {row.applied.length === 0 ? (
            <p>{t('nutrition.sessions.detail.combos.noProducts')}</p>
          ) : (
            <ul className="ntr-fsd-combo-detail__applied">
              {row.applied.map((line) => (
                <li key={line.productId} data-testid={`${testId}-applied-${line.productId}`}>
                  {line.productName}: {formatMeasure(line.applied, line.unit)}
                </li>
              ))}
            </ul>
          )}
        </div>
        {/*
          (#515) The full explanation, standing where this combo's mix preview
          would have been — because that is exactly what is missing. Manager
          copy naming the cause and the remedy, never
          nutrition.sessions.field.planUnavailable, which tells an operator in
          an orchard not to spray. The roster row carries a short pill so the
          manager does not have to expand a row to find out which operator is
          affected; this is the long form behind it.

          Prefixed with the `testId` PARAMETER, like every other testid in this
          function: jsdom renders the table detail and the phone card detail at
          once, and a testid built from row.comboId alone would be emitted twice
          (the trap this file's header documents).
        */}
        {planUnavailableCombos.has(row.comboId) && (
          <Notice tone="warn" testId={`${testId}-plan-unavailable`}>
            {t(PLAN_UNAVAILABLE_KEY[planUnavailableCombos.get(row.comboId)!]
              ?? PLAN_UNAVAILABLE_UNMEASURED_KEY)}
          </Notice>
        )}
        {preview && <NutritionMixPreviewCard preview={preview} />}
        {!isSignedOff && (
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            data-testid={`${testId}-remove`}
            onClick={() => handleRemoveCombo(row.comboId)}
          >
            {t('nutrition.sessions.detail.removeCombo')}
          </button>
        )}
      </div>
    );
  };

  const comboColumns: DataTableColumn<ComboRow>[] = [
    {
      key: 'operator',
      label: t('nutrition.sessions.detail.combos.colOperator'),
      render: (r) => <span data-testid={`fsd-combo-operator-${r.comboId}`}>{r.operatorName}</span>,
    },
    {
      key: 'machine',
      label: t('nutrition.sessions.detail.combos.colMachine'),
      render: (r) => <span data-testid={`fsd-combo-machine-${r.comboId}`}>{r.machineName}</span>,
    },
    {
      key: 'plannedTanks',
      label: t('nutrition.sessions.detail.combos.colTanksPlanned'),
      align: 'right',
      // (#461/D5) THIS combo's own planned tank count, against its own
      // machine — never a session-level figure (see the identity block's
      // own comment on why that column is gone).
      render: (r) => (
        <>
          <span data-testid={`fsd-combo-planned-tanks-${r.comboId}`}>{plannedTanksLabel(r)}</span>
          {/*
            (#515) The reason the cell above is a dash, on the roster itself.
            Without it a manager loading the session sees an unexplained em
            dash indistinguishable from any other blank, and would have to
            expand every row to find which operator is broken. The printed
            sheet names its un-printable combo unconditionally (spec §4.3) and
            these two surfaces must not disagree. Short by design — the full
            explanation is the Notice in the expanded detail.
          */}
          {planUnavailableCombos.has(r.comboId) && (
            <>
              {' '}
              <span data-testid={`fsd-combo-no-plan-${r.comboId}`}>
                <StatusPill variant="warn">
                  {t('nutrition.sessions.detail.comboPlanUnavailableShort')}
                </StatusPill>
              </span>
            </>
          )}
        </>
      ),
    },
    {
      key: 'startedAt',
      label: t('nutrition.sessions.detail.combos.colStarted'),
      render: (r) => (r.startedAt ? formatDateTime(r.startedAt) : '—'),
    },
    {
      key: 'endedAt',
      label: t('nutrition.sessions.detail.combos.colCompleted'),
      render: (r) => (r.endedAt ? formatDateTime(r.endedAt) : '—'),
    },
    {
      key: 'tanksApplied',
      label: t('nutrition.sessions.detail.combos.colTanksApplied'),
      align: 'right',
      // `?? '—'`, never `|| '—'` — a genuine captured 0 must render as 0.
      render: (r) => <span data-testid={`fsd-tanks-applied-${r.comboId}`}>{r.tanksApplied ?? '—'}</span>,
    },
    {
      key: 'status',
      label: t('nutrition.sessions.detail.combos.colStatus'),
      render: (r) => (
        <span data-testid={`fsd-combo-status-${r.comboId}`}>
          <StatusPill variant={COMBO_STATUS_VARIANT[r.status] ?? 'info'}>
            {t(`nutrition.sessions.field.status.${r.status}`)}
          </StatusPill>
          {' '}
          {/*
            Provenance belongs on the COMBO, not the session: completeFoliar
            sets captureMode on the combo, and one combo can be back-captured
            while another was captured live. The page previously read
            session.captureMode, which nothing on the foliar path ever writes —
            so this marker never rendered at all.
          */}
          <span data-testid={`fsd-combo-capture-${r.comboId}`}>
            <NutritionBackCapturePill captureMode={r.captureMode} />
          </span>
        </span>
      ),
    },
    {
      key: 'record',
      label: '',
      // Offered only while the combo is still live. A terminal combo no longer
      // matches completeFoliar's status filter and would 404, so the affordance
      // must not render rather than round-trip to a predictable failure.
      render: (r) => (
        !isSignedOff && canRecordCombo(r.status) ? (
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            data-testid={`fsd-record-${r.comboId}`}
            onClick={() => openRecord(r)}
          >
            {t('nutrition.sessions.detail.record')}
          </button>
        ) : null
      ),
    },
    {
      key: 'cancel',
      label: '',
      // Rendered only while the combo is still live (#476) — a terminal
      // combo (completed/incomplete/cancelled) would 409, so the affordance
      // must not appear rather than let the request round-trip to a
      // predictable failure. Not gated on window.confirm: a stand-down needs
      // a typed reason, which a confirm() dialog cannot capture.
      render: (r) => (
        !isSignedOff && canCancelCombo(r.status) ? (
          <span data-testid={`combo-row-${r.comboId}`}>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              data-testid={`combo-row-${r.comboId}-cancel`}
              onClick={() => setCancelTarget(r)}
            >
              {t('nutrition.sessions.detail.cancelCombo')}
            </button>
          </span>
        ) : null
      ),
    },
    {
      key: 'expand',
      label: '',
      render: (r) => (
        <button
          type="button"
          className="pat-btn pat-btn--subtle"
          data-testid={`fsd-expand-${r.comboId}`}
          aria-expanded={expandedCombos.has(r.comboId)}
          aria-label={t('nutrition.sessions.detail.combos.expandToggle', { name: `${r.operatorName} · ${r.machineName}` })}
          onClick={(e) => { e.stopPropagation(); toggleCombo(r.comboId); }}
        >
          {expandedCombos.has(r.comboId) ? '▾' : '▸'}
        </button>
      ),
    },
  ];

  const varianceColumns: DataTableColumn<VarianceLine>[] = [
    { key: 'product', label: t('nutrition.sessions.detail.variance.colProduct'), render: (l) => l.productName },
    {
      key: 'planned',
      label: t('nutrition.sessions.detail.variance.colPlanned'),
      align: 'right',
      render: (l) => formatMeasure(l.planned, l.unit),
    },
    {
      key: 'applied',
      label: t('nutrition.sessions.detail.variance.colApplied'),
      align: 'right',
      render: (l) => formatMeasure(l.applied, l.unit),
    },
    {
      key: 'variance',
      label: t('nutrition.sessions.detail.variance.colVariance', { pct: VARIANCE_MARKER_PCT }),
      align: 'right',
      // The default case must be quiet (Ruling 5): the marker (and its
      // testid) render ONLY for a warn tone. deltaPct === null renders '—',
      // never Infinity/NaN.
      render: (l) => {
        if (l.deltaPct === null) return '—';
        const tone = varianceTone(l.deltaPct);
        const text = `${l.deltaPct > 0 ? '+' : ''}${l.deltaPct}%`;
        return tone === 'warn' ? (
          <span data-testid={`fsd-variance-${l.productId}`} className="ntr-fsd-variance-marker">{text}</span>
        ) : text;
      },
    },
  ];

  return (
    <div className="pat-page" data-testid="fsd-page">
      <PageHeader
        title={`${session.orchardNo} · ${session.roundName}`}
        meta={(
          <div className="ntr-fsd-meta">
            <span data-testid="fsd-status">
              <StatusPill variant={SESSION_STATUS_VARIANT[session.status] ?? 'info'}>
                {t(`nutrition.sessions.status.${session.status}`)}
              </StatusPill>
            </span>
            <span className="ntr-fsd-chip" data-testid="fsd-round-chip">
              <Link to={`/nutrition/foliar/programmes/${session.programmeId}/rounds/${session.roundId}`}>
                {session.roundName}
              </Link>
            </span>
            <span className="ntr-fsd-chip" data-testid="fsd-program-chip">
              <Link to={`/nutrition/foliar?programme=${session.programmeId}`}>
                {t('nutrition.sessions.detail.identity.program')}
              </Link>
            </span>
            {session.status === 'completed' && waiting && (
              <span className="ntr-fsd-waiting" data-testid="fsd-waiting-signoff">
                <StatusPill variant={waiting.tone}>
                  {t('nutrition.sessions.detail.waitingSignoff', { count: waiting.days })}
                </StatusPill>
              </span>
            )}
          </div>
        )}
        actions={(
          <>
            <button
              type="button"
              className="pat-btn"
              data-testid="fsd-print"
              onClick={handlePrintInstructions}
              disabled={!canPrintInstruction(detail) || printing}
            >
              {t('nutrition.sessions.printInstructions')}
            </button>
            {session.status === 'completed' && (
              <button
                type="button"
                className="pat-btn pat-btn--primary"
                data-testid="fsd-signoff"
                onClick={handleSignOff}
                disabled={signingOff}
              >
                {signingOff
                  ? t('nutrition.sessions.detail.signingOff')
                  : t('nutrition.sessions.detail.signOffBtn')}
              </button>
            )}
            {canDeleteSession(session.status) && (
              <button
                type="button"
                className="pat-btn pat-btn--danger"
                data-testid="fsd-delete"
                onClick={() => setDeleteOpen(true)}
              >
                {t('nutrition.sessions.detail.deleteBtn')}
              </button>
            )}
          </>
        )}
      />

      {printError && <Notice tone="danger" testId="fsd-print-error">{printError}</Notice>}
      {signOffError && <Notice tone="danger" testId="fsd-signoff-error">{signOffError}</Notice>}

      <div className="ntr-card">
        <div className="ntr-card__header">
          <h3 className="ntr-card__title">{t('nutrition.sessions.detail.identity.title')}</h3>
        </div>
        <div className="ntr-card__body">
          <DefinitionList columns={2}>
            <DataPair
              label={t('nutrition.sessions.detail.identity.orchard')}
              value={<span data-testid="fsd-orchard">{session.orchardNo}</span>}
            />
            <DataPair
              label={t('nutrition.sessions.detail.identity.cultivar')}
              value={<span data-testid="fsd-cultivar">{orchard.cultivar ?? '—'}</span>}
            />
            <DataPair
              label={t('nutrition.sessions.detail.identity.area')}
              value={<span data-testid="fsd-area">{orchard.areaHa != null ? `${orchard.areaHa} ha` : '—'}</span>}
            />
            <DataPair
              label={t('nutrition.sessions.detail.identity.trees')}
              value={<span data-testid="fsd-trees">{orchard.trees ?? '—'}</span>}
            />
            <DataPair
              label={t('nutrition.sessions.detail.identity.water')}
              value={(
                <span data-testid="fsd-water">
                  {session.waterL != null ? `${session.waterL} L` : '—'}
                </span>
              )}
            />
            {/*
              (#461/D5) The single "Tanks" (planned) row is GONE — there is
              no session-level figure once combos can carry different
              machines. Each combo's own plannedTankCount now renders in the
              combos table/card below, against the machine it belongs to.
              "Tanks applied" stays here: it is an unambiguous SUM of
              captured tanks across combos, not a per-machine planned figure.
            */}
            <DataPair
              label={t('nutrition.sessions.detail.identity.tanksApplied')}
              value={<span data-testid="fsd-tanks-applied">{appliedTotal ?? '—'}</span>}
            />
            <DataPair
              label={t('nutrition.sessions.detail.identity.round')}
              value={(
                <Link data-testid="fsd-round" to={`/nutrition/foliar/programmes/${session.programmeId}/rounds/${session.roundId}`}>
                  {session.roundName}
                </Link>
              )}
            />
            <DataPair
              label={t('nutrition.sessions.detail.identity.program')}
              value={(
                <Link data-testid="fsd-program" to={`/nutrition/foliar?programme=${session.programmeId}`}>
                  {t('nutrition.sessions.detail.identity.viewProgram')}
                </Link>
              )}
            />
            <DataPair
              label={t('nutrition.sessions.detail.identity.date')}
              value={<span data-testid="fsd-date">{session.date ? formatDateTime(session.date) : '—'}</span>}
            />
            <DataPair
              label={t('nutrition.sessions.detail.identity.startedAt')}
              value={<span data-testid="fsd-started-at">{session.startedAt ? formatDateTime(session.startedAt) : '—'}</span>}
            />
            <DataPair
              label={t('nutrition.sessions.detail.identity.completedAt')}
              value={<span data-testid="fsd-completed-at">{session.completedAt ? formatDateTime(session.completedAt) : '—'}</span>}
            />
            <DataPair
              label={t('nutrition.sessions.detail.identity.signedOffAt')}
              value={<span data-testid="fsd-signed-off-at">{session.signedOffAt ? formatDateTime(session.signedOffAt) : '—'}</span>}
            />
          </DefinitionList>
        </div>
      </div>

      {!isSignedOff && (
        <FormSection id="fsd-assign" title={t('nutrition.sessions.detail.assignTitle')}>
          {assignError && <Notice tone="danger" testId="fsd-assign-error">{assignError}</Notice>}
          <div className="ntr-assign-form">
            <div className="ntr-field">
              <label className="ntr-meta-grid__label" htmlFor="fsd-assign-operator">
                {t('nutrition.sessions.detail.operatorLabel')}
              </label>
              <select
                id="fsd-assign-operator"
                className="pat-input"
                value={selectedOperator}
                onChange={(e) => setSelectedOperator(e.target.value)}
                aria-label={t('nutrition.sessions.detail.operatorLabel')}
              >
                <option value="">{t('nutrition.sessions.detail.selectOperator')}</option>
                {operators.map((op) => (
                  <option key={op._id} value={op._id}>{op.name}</option>
                ))}
              </select>
            </div>
            <div className="ntr-field">
              <label className="ntr-meta-grid__label" htmlFor="fsd-assign-machine">
                {t('nutrition.sessions.detail.machineLabel')}
              </label>
              <select
                id="fsd-assign-machine"
                className="pat-input"
                value={selectedMachine}
                onChange={(e) => setSelectedMachine(e.target.value)}
                aria-label={t('nutrition.sessions.detail.machineLabel')}
              >
                <option value="">{t('nutrition.sessions.detail.selectMachine')}</option>
                {machines.map((m) => (
                  <option key={m._id} value={m._id}>{m.name}</option>
                ))}
              </select>
              {/*
                (#515) Warn, do not refuse. A machine with no tankVolume is a
                legitimate record — the field is optional on Protection's
                SprayMachine model — but assignCombo writes plannedPerTankMix
                only when the machine has one, and without it buildMixPreview
                raises PLAN_UNAVAILABLE and transitionCombo refuses to complete
                the combo. Say that before the manager assigns, rather than
                leaving them to discover it on the row. Guarded on a chosen
                machine: nothing selected is not the same fact as a tank-less
                machine.

                FALSINESS, not `== null`. assignCombo's guard is
                `parent.plannedMix && machine.tankVolume`, so a machine recorded
                with tankVolume 0 produces exactly the broken combo this warning
                exists to pre-empt, and `== null` would stay silent on it.
                Protection treats a missing or zero tank volume alike (spec
                §2.2); the warning has to agree with the guard it predicts.
              */}
              {selectedMachine
                && !machines.find((m) => m._id === selectedMachine)?.tankVolume && (
                <Notice tone="warn" testId="fsd-assign-no-tank">
                  {t('nutrition.sessions.detail.assignNoTankVolume')}
                </Notice>
              )}
            </div>
            <div className="ntr-actions">
              <button
                type="button"
                className="pat-btn pat-btn--primary"
                data-testid="fsd-assign"
                onClick={handleAssign}
                disabled={!selectedOperator || !selectedMachine || assigning}
              >
                {assigning
                  ? t('nutrition.sessions.detail.assigning')
                  : t('nutrition.sessions.detail.assignBtn')}
              </button>
            </div>
          </div>
        </FormSection>
      )}

      <div className="ntr-card">
        <div className="ntr-card__header">
          <h3 className="ntr-card__title">{t('nutrition.sessions.detail.combos.title')}</h3>
        </div>
        <div className="ntr-card__body">
          {/*
            DataTable hard-codes data-testid="data-table-cards" /
            "data-table-footer" on ITS OWN wrapper (same category of trap as
            DefinitionList's hard-coded "definition-list" id) — with a SECOND
            card-bearing DataTable on this page (the variance table below),
            an unscoped `getByTestId('data-table-cards')` would be ambiguous.
            This wrapper testid, plus `within()`, disambiguates without
            touching the shared primitive.
          */}
          <div data-testid="fsd-combos">
            <DataTable<ComboRow>
              columns={comboColumns}
              rows={combos}
              rowKey={(r) => r.comboId}
              emptyState={t('nutrition.sessions.detail.combos.empty')}
              expandedRowRender={(r) => renderComboDetail(r, `fsd-detail-${r.comboId}`)}
              expandedRowKey={expandedCombos}
              mobileCardRow={(r) => (
                <div className="ntr-fsd-combo-card" data-testid={`fsd-combo-card-${r.comboId}`}>
                  <div className="ntr-fsd-combo-card__head">
                    <span className="ntr-fsd-combo-card__title">{r.operatorName} · {r.machineName}</span>
                    <StatusPill variant={COMBO_STATUS_VARIANT[r.status] ?? 'info'}>
                      {t(`nutrition.sessions.field.status.${r.status}`)}
                    </StatusPill>
                    {/* Mirrors the desktop Status column's provenance span
                        (see the comment there) — same r.captureMode source,
                        never session.captureMode. */}
                    <span data-testid={`fsd-combo-capture-card-${r.comboId}`}>
                      <NutritionBackCapturePill captureMode={r.captureMode} />
                    </span>
                  </div>
                  <div className="ntr-fsd-combo-card__facts">
                    <span>{t('nutrition.sessions.detail.combos.colStarted')}: {r.startedAt ? formatDateTime(r.startedAt) : '—'}</span>
                    <span>{t('nutrition.sessions.detail.combos.colCompleted')}: {r.endedAt ? formatDateTime(r.endedAt) : '—'}</span>
                    <span>
                      {/* Mirrors the desktop table's plannedTanks column — this
                          combo's own figure, against its own machine (see that
                          column's own comment). */}
                      {t('nutrition.sessions.detail.combos.colTanksPlanned')}:
                      {' '}
                      <span data-testid={`fsd-combo-planned-tanks-card-${r.comboId}`}>{plannedTanksLabel(r)}</span>
                      {/* Mirrors the desktop plannedTanks column's no-plan pill
                          (see the comment there) — Patterns.css hides the whole
                          table below 768px once a mobileCardRow exists, so a
                          signal living only in the column is invisible on a
                          phone. */}
                      {planUnavailableCombos.has(r.comboId) && (
                        <>
                          {' '}
                          <span data-testid={`fsd-combo-no-plan-card-${r.comboId}`}>
                            <StatusPill variant="warn">
                              {t('nutrition.sessions.detail.comboPlanUnavailableShort')}
                            </StatusPill>
                          </span>
                        </>
                      )}
                    </span>
                    <span>
                      {t('nutrition.sessions.detail.combos.colTanksApplied')}:
                      {' '}
                      <span data-testid={`fsd-tanks-applied-card-${r.comboId}`}>{r.tanksApplied ?? '—'}</span>
                    </span>
                  </div>
                  {/* Mirrors the desktop Record column's guard exactly (see
                      the comment there) — do not re-derive the condition. */}
                  {!isSignedOff && canRecordCombo(r.status) ? (
                    <button
                      type="button"
                      className="pat-btn pat-btn--subtle"
                      data-testid={`fsd-record-card-${r.comboId}`}
                      onClick={(e) => { e.stopPropagation(); openRecord(r); }}
                    >
                      {t('nutrition.sessions.detail.record')}
                    </button>
                  ) : null}
                  {/* Mirrors the desktop Cancel column's guard exactly (see the
                      comment there) — do not re-derive the condition. The
                      desktop column is unreachable below 768px because
                      Patterns.css hides the whole table once a mobileCardRow
                      exists, which is #490. */}
                  {!isSignedOff && canCancelCombo(r.status) ? (
                    <button
                      type="button"
                      className="pat-btn pat-btn--subtle"
                      data-testid={`combo-row-card-${r.comboId}-cancel`}
                      onClick={(e) => { e.stopPropagation(); setCancelTarget(r); }}
                    >
                      {t('nutrition.sessions.detail.cancelCombo')}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="pat-btn pat-btn--subtle"
                    data-testid={`fsd-expand-card-${r.comboId}`}
                    aria-expanded={expandedCombos.has(r.comboId)}
                    aria-label={t('nutrition.sessions.detail.combos.expandToggle', { name: `${r.operatorName} · ${r.machineName}` })}
                    onClick={(e) => { e.stopPropagation(); toggleCombo(r.comboId); }}
                  >
                    {expandedCombos.has(r.comboId) ? '▾' : '▸'}
                  </button>
                  {expandedCombos.has(r.comboId) && renderComboDetail(r, `fsd-detail-card-${r.comboId}`)}
                </div>
              )}
            />
          </div>
        </div>
      </div>

      <div className="ntr-card">
        <div className="ntr-card__header">
          <h3 className="ntr-card__title">{t('nutrition.sessions.detail.variance.title')}</h3>
        </div>
        <div className="ntr-card__body">
          {/* See the comment on the combos table's wrapper above — this is
              the SECOND card-bearing DataTable on the page, and it needs its
              own card view: this table (product / planned / applied /
              variance) is the headline comparison the whole screen exists to
              show, so a phone must not fall back to a 4-column
              horizontally-scrolling table for it. */}
          <div data-testid="fsd-variance">
            <DataTable<VarianceLine>
              columns={varianceColumns}
              rows={varianceLines}
              rowKey={(l) => l.productId}
              emptyState={t('nutrition.sessions.detail.variance.empty')}
              footerLabel={t('nutrition.sessions.detail.variance.totalsLabel')}
              // Built BY KEY, never a positional literal, so reordering a
              // column moves its total with it.
              footerRow={varianceColumns.map((c) => {
                switch (c.key) {
                  case 'product': return t('nutrition.sessions.detail.variance.totalsLabel');
                  case 'planned': return totalPlanned == null ? '—' : String(totalPlanned);
                  case 'applied': return String(totalApplied);
                  default: return null;
                }
              })}
              // Carries the SAME four figures as the columns — product,
              // planned, applied, and the variance marker — so the phone
              // card is never a lesser view of the comparison than the
              // desktop table.
              mobileCardRow={(l) => {
                const tone = varianceTone(l.deltaPct);
                const varianceText = l.deltaPct === null ? '—' : `${l.deltaPct > 0 ? '+' : ''}${l.deltaPct}%`;
                return (
                  <div className="ntr-fsd-variance-card" data-testid={`fsd-variance-card-${l.productId}`}>
                    <span className="ntr-fsd-variance-card__product">{l.productName}</span>
                    <span className="ntr-fsd-variance-card__figures">
                      {t('nutrition.sessions.detail.variance.colPlanned')}: {formatMeasure(l.planned, l.unit)}
                      {' · '}
                      {t('nutrition.sessions.detail.variance.colApplied')}: {formatMeasure(l.applied, l.unit)}
                    </span>
                    <span
                      data-testid={tone === 'warn' ? `fsd-variance-card-marker-${l.productId}` : undefined}
                      className={tone === 'warn' ? 'ntr-fsd-variance-marker' : undefined}
                    >
                      {varianceText}
                    </span>
                  </div>
                );
              }}
            />
          </div>
        </div>
      </div>

      <Modal show={deleteOpen} onHide={() => setDeleteOpen(false)} title={t('nutrition.sessions.detail.deleteTitle')}>
        <div data-testid="fsd-delete-modal">
          <Modal.Body>
            <p data-testid="fsd-delete-stock-warning">
              {t('nutrition.sessions.detail.deleteStockWarning')}
            </p>
            <p>{t('nutrition.sessions.detail.deleteBody')}</p>
            {deleteError && <Notice tone="danger" testId="fsd-delete-error">{deleteError}</Notice>}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="pat-btn" onClick={() => setDeleteOpen(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--danger"
              data-testid="fsd-delete-confirm"
              disabled={deleting}
              onClick={handleDelete}
            >
              {t('nutrition.sessions.detail.deleteConfirm')}
            </button>
          </Modal.Footer>
        </div>
      </Modal>

      <Modal
        show={cancelTarget !== null}
        onHide={closeCancelModal}
        title={t('nutrition.sessions.detail.cancelComboTitle')}
      >
        <div data-testid="fsd-cancel-modal">
          <Modal.Body>
            <p>
              {t('nutrition.sessions.detail.cancelComboBody', { operator: cancelTarget?.operatorName })}
            </p>
            <Field
              label={t('nutrition.sessions.detail.cancelComboReason')}
              controlId="fsd-cancel-reason"
              required
            >
              <textarea
                id="fsd-cancel-reason"
                className="pat-input"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </Field>
            {/*
              Global Constraint 14: Modal portals to document.body, so this
              MUST render inside Modal.Body — a page-level Notice sits
              underneath the modal scrim and the user never sees it. The
              modal stays open on failure so the manager can read why.
            */}
            {cancelError && <Notice tone="danger" testId="combo-cancel-error">{cancelError}</Notice>}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="pat-btn" onClick={closeCancelModal}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--danger"
              data-testid="combo-cancel-confirm"
              disabled={!cancelReason.trim() || cancelling}
              onClick={handleCancelCombo}
            >
              {t('nutrition.sessions.detail.cancelComboConfirm')}
            </button>
          </Modal.Footer>
        </div>
      </Modal>

      <Modal
        show={recordTarget !== null}
        onHide={closeRecord}
        title={t('nutrition.sessions.detail.recordTitle')}
      >
        <div data-testid="fsd-record-modal">
          <Modal.Body>
            <Field label={t('nutrition.sessions.detail.recordDate')} controlId="fsd-record-date" required>
              <input
                id="fsd-record-date"
                type="date"
                className="pat-input"
                data-testid="fsd-record-date"
                value={recordDate}
                max={new Date().toISOString().slice(0, 10)}
                disabled={recordBusy}
                onChange={(e) => setRecordDate(e.target.value)}
              />
            </Field>

            {isBackCaptureBlocked(recordDate, beyondOverrideOn) && (
              <Notice tone="warn" testId="fsd-record-too-old">
                {t('nutrition.sessions.detail.recordTooOld')}
              </Notice>
            )}

            <Field label={t('nutrition.sessions.detail.recordTanks')} controlId="fsd-record-tanks" required>
              <input
                id="fsd-record-tanks"
                type="number"
                min="0"
                step="0.01"
                className="pat-input"
                data-testid="fsd-record-tanks"
                value={recordTanks}
                disabled={recordBusy}
                onChange={(e) => setRecordTanks(e.target.value)}
              />
            </Field>

            {/*
              Global Constraint: Modal portals to document.body, so this MUST
              render inside Modal.Body — a page-level Notice sits underneath the
              scrim and is never seen. The modal stays open on failure so the
              manager can read why and retry without re-typing.
            */}
            {recordError && <Notice tone="danger" testId="fsd-record-error">{recordError}</Notice>}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="pat-btn" onClick={closeRecord} disabled={recordBusy}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--primary"
              data-testid="fsd-record-submit"
              disabled={
                !recordDate
                // A NUMERIC test, not `!recordTanks` (R18). The state is a
                // string, so a typed `0` — and `-1`, and `abc` — is truthy and
                // sailed past the gate into a 400 TANK_COUNT_REQUIRED, which
                // falls through every catch branch in handleRecord to the
                // generic "Could not record this application." This is the one
                // place the UI can still be refused without surfacing why, and
                // the server's rule (D3/R5: a supplied 0 is an observation of
                // nothing and must not fall back to the plan) is exactly this
                // predicate.
                || !(Number(recordTanks) > 0)
                || recordBusy
                || isBackCaptureBlocked(recordDate, beyondOverrideOn)
              }
              onClick={handleRecord}
            >
              {recordBusy
                ? t('nutrition.sessions.detail.recordSaving')
                : t('nutrition.sessions.detail.recordSubmit')}
            </button>
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
};

export default NutritionSessionDetailPage;
